'use client';

import { useCallback, useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import {
    setFilters,
    getWorkspaceData,
    getListingData
} from '@/hotdesk-ui/store/slices/listing.slice';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';

/**
 * Workspace-type pills now live in the global header, but the landing page
 * renders the results. Both read the selection from `listing.filters.typeIn`
 * so there is a single source of truth: on `/` a pill re-queries the carousels
 * in place, anywhere else it navigates to explore with the type applied.
 */
export const useWorkspaceTypeFilter = () => {
    const dispatch = useAppDispatch();
    const router = useRouter();
    const pathname = usePathname();

    const filters = useAppSelector((state: RootState) => state.listing.filters);
    const geoLocation = useAppSelector(
        (state: RootState) => state.geoLocation.geoLocationZipData
    );

    const selectedTypes = useMemo(() => {
        const raw = (filters as Record<string, unknown> | undefined)?.typeIn;
        if (!raw) return [] as number[];
        return String(raw)
            .split(',')
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0);
    }, [filters]);

    const isLanding = pathname === '/';

    const applyInPlace = useCallback(
        (types: number[]) => {
            const lastSearch = geoLocation || undefined;
            if (types.length === 0) {
                dispatch(setFilters({}));
                if (lastSearch)
                    dispatch(getWorkspaceData({ lastSearch, page: 1 }));
                return;
            }
            const next = { typeIn: types.join(',') };
            dispatch(setFilters(next));
            if (lastSearch)
                dispatch(
                    getListingData({ lastSearch, page: 1, filters: next })
                );
        },
        [dispatch, geoLocation]
    );

    const goToExplore = useCallback(
        (types: number[]) => {
            const params = new URLSearchParams();
            if (types.length > 0) {
                params.set('typeIn', types.join(','));
                params.set('active', Carousel.listing);
            }
            const query = params.toString();
            router.push(query ? `/workspaces?${query}` : '/workspaces');
        },
        [router]
    );

    // A landing filter re-queries in place, so send the user back to the top
    // to see the fresh results from the start.
    const scrollToTop = useCallback(() => {
        if (typeof window === 'undefined') return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const selectAll = useCallback(() => {
        if (isLanding) {
            applyInPlace([]);
            scrollToTop();
        } else goToExplore([]);
    }, [isLanding, applyInPlace, goToExplore, scrollToTop]);

    const toggleType = useCallback(
        (typeId: number) => {
            const next = selectedTypes.includes(typeId)
                ? selectedTypes.filter((value) => value !== typeId)
                : [...selectedTypes, typeId];
            if (isLanding) {
                applyInPlace(next);
                scrollToTop();
            } else goToExplore(next);
        },
        [selectedTypes, isLanding, applyInPlace, goToExplore, scrollToTop]
    );

    return { selectedTypes, selectAll, toggleType };
};

export default useWorkspaceTypeFilter;
